import React, { useEffect, useState, useCallback, useRef } from "react";
import { MoreHorizontal } from "lucide-react";
import OrdersRow from "./TradeTableComponents/OrdersRow";
import TradesRow from "./TradeTableComponents/TradesRow";
import { useSelector, useDispatch } from "react-redux";
import { queryContract, getTradesWithInfos } from "../functions/Contract";
import { fetchOpenTradesPnl, parseTradeIndex } from "../functions/graphqlPnl";
import HistoryRow from "./TradeTableComponents/HistoryRow";
import { useWalletConnection } from "../../hooks/useWalletConnection";
import { useSmartTradeStateDetector } from "../../hooks/useSmartTradeStateDetector";
import { useTradeHitDetector } from "../../hooks/useTradeHitDetector";
import { getTradeIndexNumber } from "../../utils/tradeNavigation";
import { TRADES_GRID_COLUMNS, TRADES_MIN_WIDTH, TRADES_ROW_PADDING_X } from "./TradeTableComponents/tableLayout";

/**
 * The empty state a tab shows when its list has nothing in it.
 *
 * One component for all three tabs, because a tab that silently renders a
 * header row over nothing reads as still loading. Only Trades had this line;
 * Orders and History dropped straight from the header into the card's padding.
 *
 * `--ink-3` rather than the `#959595` this used to be written as: inside the
 * Pokédex the token resolves to the device's quiet ink, and a fixed grey there
 * sits at the wrong value against the casing.
 */
const TableEmpty = ({ children }) => (
  <div
    className="text-center py-8"
    style={{ color: "var(--ink-3)", fontFamily: "General Sans", fontSize: 14 }}
  >
    {children}
  </div>
);

const TradingTable = ({
  liquidationPrices,
  setUpdateModalOpen,
  market,
  highlightTradeIndex,
  autoOpenDetail,
}) => {
  const [list, setList] = useState(0);
  const [trades, setTrades] = useState([]);
  const [pendingOpenIds, setPendingOpenIds] = useState([]);
  const [lastFetchTime, setLastFetchTime] = useState(0);
  const [highlightedTradeId, setHighlightedTradeId] = useState(null);
  const [consumedHighlightIndex, setConsumedHighlightIndex] = useState(null);
  const [autoOpenDetailTradeId, setAutoOpenDetailTradeId] = useState(null);
  const highlightTimeoutRef = useRef(null);
  const tradeRowRefs = useRef({});
  const prevPerpsUpdateRef = useRef(undefined);
  
  const account = useSelector((state) => state.auth.account);
  const bech32Address = useSelector((state) => state.auth.bech32Address);
  const perpsUpdate = useSelector((state) => state.trade.update);
  const { walletType } = useWalletConnection();
  const dispatch = useDispatch();
  
  // ZERO POLLING: Only state detection
  const { detectStateChanges } = useSmartTradeStateDetector();
  
  // NEW: Single hook for all hit detection
  const { checkAllTradeHits } = useTradeHitDetector(trades);
  
  const getTrades = useCallback(async (force = false) => {
    if (!account) {
      setTrades([]);
      return;
    }

    // Prevent duplicate calls within 2 seconds (force bypasses — e.g. after a close)
    const now = Date.now();
    if (!force && now - lastFetchTime < 2000) {
      console.log('⏸️ Skipping duplicate trade load');
      return;
    }
    setLastFetchTime(now);

    try {
      console.log('📊 Loading trades for market:', market);

      // Open positions/orders are always fetched fresh; closed history comes from
      // the per-account cache (instant) unless `force` (a trade just closed).
      // Alongside, one bulk request for open trades' authoritative on-chain PnL
      // — same source TradeDetailModal uses — so this table's PNL column
      // matches the modal instead of the fee-blind client-side price-tick
      // estimate TradesRow falls back to. One request per refresh, not per row.
      const [allTrades, onchainPnlById] = await Promise.all([
        getTradesWithInfos(account, walletType, { forceClosed: force }),
        fetchOpenTradesPnl(bech32Address),
      ]);

      if (!allTrades) {
        setTrades([]);
        return;
      }

      if (onchainPnlById.size > 0) {
        for (const trade of allTrades) {
          if (!trade.is_open) continue;
          const idx = parseTradeIndex(trade.user_trade_index);
          if (idx != null && onchainPnlById.has(idx)) {
            trade.onchainPnl = onchainPnlById.get(idx);
          }
        }
      }

      // Filter by market and sort by timestamp (from trade_info if available)
      const filteredTrades = allTrades
        .filter((trade) => trade.market_index === market)
        .sort((a, b) => {
          // Sort by timestamp if available
          const timestampA = a.trade_info?.last_oi_update_ts || 0;
          const timestampB = b.trade_info?.last_oi_update_ts || 0;
          
          return Number(timestampB) - Number(timestampA);
        });
      
      // SMART DETECTION: Check for state changes
      const hasChanges = detectStateChanges(filteredTrades);
      
      console.log(`✅ Loaded ${filteredTrades.length} trades${hasChanges ? ' (changes detected)' : ''}`);
      setTrades(filteredTrades);
    } catch (error) {
      console.error("Error in getTrades:", error);
      setTrades([]);
    }
  }, [account, walletType, bech32Address, market, lastFetchTime, detectStateChanges]);

  useEffect(() => {
    // Force a closed-history refresh only when a trade action occurred (perpsUpdate
    // bumped) AFTER mount. The initial mount (ref still undefined) reuses the cache
    // so navigating in / switching city opens instantly instead of re-scanning.
    const prev = prevPerpsUpdateRef.current;
    const actionHappened = prev !== undefined && prev !== perpsUpdate;
    prevPerpsUpdateRef.current = perpsUpdate;
    getTrades(actionHappened);
    // bech32Address is in deps deliberately: it resolves asynchronously after
    // `account` connects, and getTrades needs it for the bulk on-chain PnL
    // fetch. Without this, a first run before it resolved would never retry.
  }, [account, perpsUpdate, market, bech32Address]);

  // Reconcile after a background revalidation of a reload-hydrated cache.
  useEffect(() => {
    const onRevalidated = () => getTrades(false);
    window.addEventListener("tradesRevalidated", onRevalidated);
    return () => window.removeEventListener("tradesRevalidated", onRevalidated);
  }, [getTrades]);

  // Handle trade highlighting when highlightTradeIndex prop changes
  useEffect(() => {
    if (!highlightTradeIndex || trades.length === 0) {
      return;
    }

    // If we've already consumed this highlight index, don't re-apply it
    if (
      consumedHighlightIndex &&
      consumedHighlightIndex.toString() === highlightTradeIndex.toString()
    ) {
      return;
    }

    // Clear any existing timeout
    if (highlightTimeoutRef.current) {
      clearTimeout(highlightTimeoutRef.current);
    }

    // Find the trade with matching index
    const tradeToHighlight = trades.find(trade => {
      const tradeIndex = getTradeIndexNumber(trade.user_trade_index);
      return tradeIndex && tradeIndex.toString() === highlightTradeIndex.toString();
    });

    if (tradeToHighlight) {
      // Mark this highlight index as consumed so it only runs once
      setConsumedHighlightIndex(highlightTradeIndex);

      setHighlightedTradeId(tradeToHighlight.user_trade_index);

      // Auto-open detail modal if navigated from AllTradesPage
      if (autoOpenDetail) {
        setAutoOpenDetailTradeId(tradeToHighlight.user_trade_index);
      }

      // Switch to the correct tab based on trade type
      if ((tradeToHighlight.trade_type === 'limit' || tradeToHighlight.trade_type === 'stop') && tradeToHighlight.is_open) {
        setList(1); // Orders tab
      } else if (!tradeToHighlight.is_open) {
        setList(2); // History tab
      } else {
        setList(0); // Trades tab
      }
      
      // Scroll to the trade row after a short delay to ensure it's rendered and tab is switched
      setTimeout(() => {
        const rowElement = tradeRowRefs.current[tradeToHighlight.user_trade_index];
        if (rowElement) {
          rowElement.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'center' 
          });
        }
      }, 500); // Increased delay to allow tab switch

      // Remove highlight after 5 seconds
      highlightTimeoutRef.current = setTimeout(() => {
        setHighlightedTradeId(null);
      }, 5000);
    }

    return () => {
      if (highlightTimeoutRef.current) {
        clearTimeout(highlightTimeoutRef.current);
      }
    };
  }, [highlightTradeIndex, trades, consumedHighlightIndex]);

  // OPTIMIZED: Single event listener for all price-triggered hit detection
  useEffect(() => {
    const handlePriceChangeHits = (event) => {
      const { newPrice, oldPrice, priceDirection } = event.detail;
      console.log(`Price change hit check: ${oldPrice} → ${newPrice} (${priceDirection})`);
      
      // CHECK ALL HITS with single function call
      const { hits, summary } = checkAllTradeHits(newPrice);
      
      if (hits.length > 0) {
        console.log(`⚡ ${summary.totalHits} hits detected!`, summary);

        // Emit specific events for each hit type
        hits.forEach(hit => {
          if (hit.category === 'tp_sl') {
            window.dispatchEvent(new CustomEvent('tpSlHitDetected', { detail: hit }));
          } else if (hit.category === 'limit_order') {
            window.dispatchEvent(new CustomEvent('limitOrderHitDetected', { detail: hit }));
          }
        });

        // A hit likely closes a position → force the closed-history refresh.
        setTimeout(() => getTrades(true), 300);
        setTimeout(() => getTrades(true), 2000);
        setTimeout(() => getTrades(true), 5000);

      } else {
        console.log('No hits detected for this price change');
        // No state change expected — cheap refresh (open fresh, closed cached).
        setTimeout(() => getTrades(false), 4000);
      }
    };

    // A trade changed state (close/cancel/execute) → force closed-history refresh.
    const handleTradeStateChange = () => {
      console.log('🔄 Trade state change detected, refreshing...');
      getTrades(true);
    };

    // TP/SL or other open-trade edits — open positions are always fetched fresh, so
    // no need to force-rescan closed history.
    const handleTradeUpdate = () => {
      console.log("Trade update event received, refreshing data...");
      getTrades(false);
    };

    // Periodic chain check — reuse the cache (open stays fresh).
    const handleBlockchainCheck = () => getTrades(false);

    const handleHitDetected = (event) => {
      const hit = event.detail;
      console.log(`🚨 ${hit.type} hit confirmed for trade ${hit.tradeIndex}`);
      // Confirmed hit → position is closing → force closed-history refresh.
      setTimeout(() => getTrades(true), 200);
    };

    // PRIMARY EVENT: Listen for price changes from topbar
    window.addEventListener('priceChangedCheckHits', handlePriceChangeHits);

    // Hit confirmation events
    window.addEventListener('tpSlHitDetected', handleHitDetected);
    window.addEventListener('limitOrderHitDetected', handleHitDetected);

    // Existing events for manual actions
    window.addEventListener('tradesStateChanged', handleTradeStateChange);
    window.addEventListener('tradeClosedBySystem', handleTradeStateChange);
    window.addEventListener('limitOrderExecuted', handleTradeStateChange);
    window.addEventListener('userTradeAction', handleTradeStateChange);
    window.addEventListener('tradeUpdated', handleTradeUpdate);
    window.addEventListener('blockchainStateCheck', handleBlockchainCheck);

    return () => {
      // Clean up all listeners
      window.removeEventListener('priceChangedCheckHits', handlePriceChangeHits);
      window.removeEventListener('tpSlHitDetected', handleHitDetected);
      window.removeEventListener('limitOrderHitDetected', handleHitDetected);
      window.removeEventListener('tradesStateChanged', handleTradeStateChange);
      window.removeEventListener('tradeClosedBySystem', handleTradeStateChange);
      window.removeEventListener('limitOrderExecuted', handleTradeStateChange);
      window.removeEventListener('userTradeAction', handleTradeStateChange);
      window.removeEventListener('tradeUpdated', handleTradeUpdate);
      window.removeEventListener('blockchainStateCheck', handleBlockchainCheck);
    };
  }, [getTrades, checkAllTradeHits]);

  // Filter trades by type - these will automatically update when state changes
  const activeOpenTrades = [
    ...trades.filter(t => t.is_open && t.trade_type === "trade"),
    ...trades.filter(t => pendingOpenIds.includes(t.user_trade_index) && !t.is_open)
  ];
  // Named rather than filtered inline in the JSX, so each tab can ask whether
  // it is empty before it renders its rows.
  const openOrders = trades?.filter(
    (t) => (t.trade_type === "limit" || t.trade_type === "stop") && t.is_open
  ) ?? [];
  const closedTrades = trades?.filter((t) => !t.is_open) ?? [];

  return (
    <div
      className="mt-4 w-full"
      style={{
        background: "var(--surface)",
        borderRadius: "18px",
        padding: "10px 14px 12px",
        boxShadow: "var(--card)",
      }}
    >
      {/* Tabs — a sunken rail holding one raised thumb, not an underline. The
          2px bottom border was the 2022 tell: an underline says "this is a
          document section", a raised pill says "this is a control". */}
      <div className="ce-seg" style={{ width: "fit-content", height: 34 }}>
        {["Trades", "Orders", "History"].map((tabName, index) => (
          <button
            key={tabName}
            type="button"
            role="tab"
            aria-selected={list === index}
            onClick={() => setList(index)}
            className="ce-seg-item"
            style={{
              padding: "0 18px",
              fontFamily: "General Sans",
              ...(list === index
                ? { background: "var(--raise)", boxShadow: "var(--pillow-sm)", color: "var(--cat-accent)" }
                : null),
            }}
          >
            {tabName}
          </button>
        ))}
      </div>

      {/* Trades Table */}
      {list === 0 && (
        <div className="w-full overflow-visible mt-3">
          {/* `overflow-x-auto` at every breakpoint, matching the Orders and
              History tables below. It used to switch to `md:overflow-visible`,
              which let the header row's `min-w-[900px]` escape the card
              whenever the left column was narrower than 900px — the row ran
              out past the rounded corner and over the page. Nothing in these
              rows is absolutely positioned, so there is nothing that needed to
              overflow in the first place. */}
          <div className="w-full overflow-x-auto overflow-y-auto max-h-[70vh] md:max-h-none">
            <div className="min-w-full">
              {/* Header row. Grid template, min-width and padding all come from
                  tableLayout so the header and TradesRow cannot drift apart. */}
              <div
                className={`w-full grid whitespace-nowrap ${TRADES_ROW_PADDING_X}`}
                style={{
                  gridTemplateColumns: TRADES_GRID_COLUMNS,
                  minWidth: TRADES_MIN_WIDTH,
                  // A table header is something you read past, not a coloured
                  // band: a recessed well in the neutral ramp, with the labels
                  // as small caps. The lavender fill and the 10%-black hairline
                  // both go — nothing on this page carries a fill and a border.
                  height: "34px",
                  borderRadius: "11px",
                  background: "var(--surface-2)",
                  boxShadow: "var(--inset-sm)",
                  fontSize: "10.5px",
                  fontFamily: "General Sans",
                  fontWeight: 600,
                  letterSpacing: ".04em",
                  textTransform: "uppercase",
                  color: "var(--ink-3)",
                }}
              >
                <div className="flex items-center justify-center">Index</div>
                <div className="flex items-center justify-center">Date</div>
                <div className="flex items-center justify-center">Pair</div>
                <div className="flex items-center justify-center">Type</div>
                <div className="flex items-center justify-center">Entry Price</div>
                <div className="flex items-center justify-center">Leverage</div>
                <div className="flex items-center justify-center">Amount</div>
                <div className="flex items-center justify-center">Size</div>
                <div className="flex items-center justify-center">TP/SL</div>
                <div className="flex items-center justify-center">PnL</div>
                <div className="flex items-center justify-center"></div>
              </div>
              <div className="w-full flex flex-col">
                {activeOpenTrades.length === 0 ? (
                  <TableEmpty>No active trades</TableEmpty>
                ) : (
                  activeOpenTrades.map((trade) => (
                    <div
                      key={trade.user_trade_index}
                      ref={(el) => {
                        if (el) tradeRowRefs.current[trade.user_trade_index] = el;
                      }}
                      className={highlightedTradeId === trade.user_trade_index ? 'bg-indigo-50 rounded-lg transition-all duration-300' : ''}
                    >
                      <TradesRow
                        trade={trade}
                        showPnlInfo={false}
                        autoOpenDetail={autoOpenDetailTradeId === trade.user_trade_index}
                      />
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Orders Table */}
      {list === 1 && (
        <div className="w-full overflow-hidden mt-3">
          <div className="w-full overflow-x-auto overflow-y-auto max-h-[70vh] md:max-h-none">
            <div className="min-w-full">
              {/* Header row */}
              <div
                className="w-full grid grid-cols-9 min-w-[800px] whitespace-nowrap"
                style={{
                  height: "34px",
                  paddingLeft: "8px",
                  paddingRight: "8px",
                  borderRadius: "11px",
                  background: "var(--surface-2)",
                  boxShadow: "var(--inset-sm)",
                  fontSize: "10.5px",
                  fontFamily: "General Sans",
                  fontWeight: 600,
                  letterSpacing: ".04em",
                  textTransform: "uppercase",
                  color: "var(--ink-3)",
                }}
              >
                <div className="flex items-center justify-center">Index</div>
                <div className="flex items-center justify-center">Date</div>
                <div className="flex items-center justify-center">Type</div>
                <div className="flex items-center justify-center">Pair</div>
                <div className="flex items-center justify-center">Amount</div>
                <div className="flex items-center justify-center">Size</div>
                <div className="flex items-center justify-center">Leverage</div>
                <div className="flex items-center justify-center">Price</div>
                <div className="flex items-center justify-center"></div>
              </div>

              <div className="w-full flex flex-col">
                {openOrders.length === 0 ? (
                  <TableEmpty>No open orders</TableEmpty>
                ) : (
                  openOrders.map((trade) => (
                    <div
                      key={trade.user_trade_index}
                      ref={(el) => {
                        if (el) tradeRowRefs.current[trade.user_trade_index] = el;
                      }}
                      className={highlightedTradeId === trade.user_trade_index ? 'bg-indigo-50 rounded-lg transition-all duration-300' : ''}
                    >
                      <OrdersRow trade={trade} />
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* History Table */}
      {list === 2 && (
        <div className="w-full overflow-hidden mt-3">
          <div className="w-full overflow-x-auto overflow-y-auto max-h-[70vh] md:max-h-none">
            <div className="min-w-full">
              {/* Header row */}
              <div
                className="w-full grid grid-cols-10 min-w-[800px] whitespace-nowrap"
                style={{
                  height: "34px",
                  paddingLeft: "8px",
                  paddingRight: "8px",
                  borderRadius: "11px",
                  background: "var(--surface-2)",
                  boxShadow: "var(--inset-sm)",
                  fontSize: "10.5px",
                  fontFamily: "General Sans",
                  fontWeight: 600,
                  letterSpacing: ".04em",
                  textTransform: "uppercase",
                  color: "var(--ink-3)",
                }}
              >
                <div className="flex items-center justify-center">Index</div>
                <div className="flex items-center justify-center">Date</div>
                <div className="flex items-center justify-center">Pair</div>
                <div className="flex items-center justify-center">Type</div>
                <div className="flex items-center justify-center">Entry Price</div>
                <div className="flex items-center justify-center">Leverage</div>
                <div className="flex items-center justify-center">Amount</div>
                <div className="flex items-center justify-center">Size</div>
                <div className="flex items-center justify-center">Exit Price</div>
                <div className="flex items-center justify-center">PnL</div>
              </div>

              <div className="w-full flex flex-col">
                {closedTrades.length === 0 ? (
                  <TableEmpty>No trade history</TableEmpty>
                ) : (
                  closedTrades.map((trade) => (
                    <div
                      key={trade.user_trade_index}
                      ref={(el) => {
                        if (el) tradeRowRefs.current[trade.user_trade_index] = el;
                      }}
                      className={highlightedTradeId === trade.user_trade_index ? 'bg-indigo-50 rounded-lg transition-all duration-300' : ''}
                    >
                      <HistoryRow
                        trade={trade}
                        showShareButton={false}
                        showPnlInfo={false}
                        autoOpenDetail={autoOpenDetailTradeId === trade.user_trade_index}
                      />
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TradingTable;
